import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import { Checkbox, DynamicIconWrapper, Icons, Input, Modal, SubmissionButtons, TextArea } from '@components/common';
import { EChipsSelectMenuPlacement } from '@components/common/GroupSelect/enum';
import ValueContent from '@components/common/GroupSelectKpi/ValueContent';

import useMutateOperationWindowRulesCheckName from '@hooks/cmModule/operationWindowRules/useMutateOperationWindowRulesCheckName';

import { useModal } from '@components/common/Modal/utils';

import { TOperationWindowRuleDTO } from '@interfaces/cmModule/dto/operationWindowRulesDTO';
import { IOperationWindowRule, IWorkingDays } from '@interfaces/cmModule/operationWindowRules';
import { IId } from '@interfaces/common/common';
import { TRange } from '@interfaces/common/range';
import { IResponseError } from '@interfaces/errors/apiErrors';
import { IGroupSelectItem } from '@components/common/GroupSelect/GroupSelect.types';

import { EYupErrorTypes } from '@enums/common';
import { EValidationMode } from '@enums/validationMode';

import { MODAL_WIDTH_375, MODAL_WIDTH_580 } from '@constants/modals';
import { MAX_50_LENGTH, MAX_500_LENGTH } from '@constants/validationMessages';
import { defaultOperationRulesSchedule } from './constants';

import OperationWindowRulesDuplicateForm from './OperationWindowRulesDuplicateForm';
import OperationWindowRulesTimeInterval from './OperationWindowRulesTimeInterval';
import { schema } from './schema';
import { checkIsAllSelectedHasRanges, checkIsEnabledDaysScheduleChanged, sortRuleRanges } from './utils';

import styles from './OperationWindowRulesForm.module.scss';

interface IOperationWindowRulesFormProps {
	operationWindowRule?: IOperationWindowRule;
	defaultSchedule?: IWorkingDays[];
	isReadOnly?: boolean;
	isWithoutNegativeButton?: boolean;
	setIsDirtyForm?: (isDirty: boolean) => void;
	onSubmit: (dto: TOperationWindowRuleDTO) => void;
}

const OperationWindowRulesForm = ({
	operationWindowRule,
	defaultSchedule = defaultOperationRulesSchedule,
	isReadOnly = false,
	isWithoutNegativeButton = false,
	setIsDirtyForm,
	onSubmit,
}: IOperationWindowRulesFormProps) => {
	const { onClose, setTitleValue } = useModal();

	const {
		checkRuleNameError,
		checkRuleName,
	} = useMutateOperationWindowRulesCheckName();

	const {
		register,
		formState: { errors },
		setError,
		watch,
	} = useForm<FieldValues>({
		mode: EValidationMode.all,
		resolver: yupResolver(schema),
		defaultValues: {
			name: operationWindowRule?.name || '',
			note: operationWindowRule?.note || '',
		},
	});

	const containerRef = useRef<HTMLDivElement>(null);

	const [scheduleFields, setScheduleFields] = useState(defaultSchedule);
	const [selectedWeekDay, setSelectedWeekDay] = useState<IWorkingDays | undefined>();

	const [isTimeIntervalModal, setIsTimeIntervalModal] = useState(false);
	const [isDuplicateModal, setIsDuplicateModal] = useState(false);
	const [isEditMode, setIsEditMode] = useState(!isReadOnly);

	const isAllSelectedHasRanges = checkIsAllSelectedHasRanges(scheduleFields);
	const isScheduleChanged = checkIsEnabledDaysScheduleChanged(scheduleFields, defaultSchedule);

	const name = watch('name');
	const note = watch('note');

	const proceedHandler = useCallback(() => {
		setIsEditMode(true);
	}, []);

	const negativeHandler = useCallback(() => {
		onClose();
	}, [onClose]);

	const openDuplicateModal = useCallback(() => {
		setIsDuplicateModal(true);
	}, []);

	const closeDuplicateModal = useCallback(() => {
		setIsDuplicateModal(false);
	}, []);

	const openTimeIntervalModal = useCallback(() => {
		setIsTimeIntervalModal(true);
	}, []);

	const closeTimeIntervalModal = useCallback(() => {
		setIsTimeIntervalModal(false);
	}, []);

	const validateNameHandler = useCallback(async (value: string) => {
		try {
			if (!value || !checkRuleName) {
				return;
			}

			await checkRuleName({ name: value });
		} catch (e) {
			const errorMessage = (e as IResponseError)?.errors[0]?.message;

			if (errorMessage) {
				setError('name', {
					type: EYupErrorTypes.required,
					message: errorMessage,
				});
			}
		}
	}, [checkRuleName, setError]);

	const handleSubmit = useCallback(() => {
		if (!isEditMode) {
			setIsEditMode(true);
			onClose();

			return;
		}

		const schedule = scheduleFields.map((day) => ({
			...day,
			categoryName: undefined,
			weekDay: day.weekDay.name,
			ranges: day.ranges.map((range) => ({
				...range,
				id: undefined,
			})),
		})).filter((day) => day.isEnabled);

		onSubmit({
			name,
			note,
			workingDays: schedule,
		});
	}, [
		onSubmit,
		onClose,
		scheduleFields,
		isEditMode,
		note,
		name,
	]);

	const duplicateDayHandler = useCallback((selectedDays: string[]) => {
		const newScheduleFields = scheduleFields.map((day) => {
			if (selectedDays.includes(day.weekDay.name)) {
				return {
					...day,
					ranges: selectedWeekDay?.ranges || [],
				};
			}

			return day;
		});

		setScheduleFields(newScheduleFields);
		setIsDuplicateModal(false);
	}, [scheduleFields, selectedWeekDay]);

	const removeOptions = useCallback((ids: string[], categoryName: string[]) => {
		categoryName.forEach((name) => {
			setScheduleFields((prev) =>
				prev.map((day) => {
					const isDayMatched = day.weekDay.name === name;

					if (!isDayMatched) {
						return day;
					}

					const ranges = day.ranges?.filter((range) => range?.id && !ids.includes(range?.id)) || [];

					return {
						...day,
						ranges,
						isEnabled: ranges.length > 0 ? day.isEnabled : false,
					};
				}),
			);
		});
	}, []);

	const handleAddRule = useCallback((range: TRange<string> & IId) => {
		if (selectedWeekDay) {
			setScheduleFields((prev) =>
				prev.map((day) =>
					day.weekDay.name === selectedWeekDay.weekDay.name
						? {
							...day,
							ranges: [...(day.ranges || []), range],
						}
						: day,
				),
			);

			setSelectedWeekDay(undefined);
			setIsTimeIntervalModal(false);
		}

	}, [selectedWeekDay]);

	const handleAddRange = (field: IWorkingDays) => () => {
		if (!isEditMode) {
			return;
		}

		openTimeIntervalModal();
		setSelectedWeekDay(field);
	};

	const handleDuplicateRange = (field: IWorkingDays) => () => {
		if (!isEditMode) {
			return;
		}

		openDuplicateModal();
		setSelectedWeekDay(field);
	};

	const handleCheckboxChange = (field: IWorkingDays) => (value: boolean) => {
		if (!isEditMode) {
			return;
		}

		setScheduleFields((prev) =>
			prev.map((day) =>
				day.weekDay.name === field.weekDay.name
					? { ...day, isEnabled: value }
					: day,
			),
		);
	};

	useEffect(() => {
		setTitleValue(isEditMode
			? `${operationWindowRule ? 'Edit' : 'Add new'} operation window rule`
			: 'View “Weekly” schedule',
		);
	}, [
		isEditMode,
		operationWindowRule,
		setTitleValue,
	]);

	useEffect(() => {
		if (setIsDirtyForm) {
			setIsDirtyForm(isScheduleChanged || name !== (operationWindowRule?.name || ''));
		}
	}, [
		isScheduleChanged,
		name,
		operationWindowRule?.name,
		setIsDirtyForm,
	]);

	const isOperationNotValid = !(isScheduleChanged && isAllSelectedHasRanges);
	const isNoteChanged = !!note && note !== operationWindowRule?.note && isAllSelectedHasRanges;
	const isFormAndRulesValidation = operationWindowRule?.name
		? operationWindowRule?.name === name
			? isOperationNotValid
			: !isAllSelectedHasRanges
		: !name || isOperationNotValid;

	const isSameName = operationWindowRule?.name === name;
	const isSubmitDisabled = !(isNoteChanged || !isFormAndRulesValidation) || !isSameName && !!errors?.name?.message;

	return (
		<div className={styles.wrapper}>
			<Modal
				isOpen={isDuplicateModal}
				onClose={closeDuplicateModal}
				title='Duplicate schedule'
				width={MODAL_WIDTH_580}
			>
				<OperationWindowRulesDuplicateForm
					disabledDay={selectedWeekDay}
					onSubmit={duplicateDayHandler}
				/>
			</Modal>
			<Modal
				isOpen={isTimeIntervalModal}
				onClose={closeTimeIntervalModal}
				title='Add time interval'
				width={MODAL_WIDTH_375}
			>
				<OperationWindowRulesTimeInterval
					existedRanges={selectedWeekDay?.ranges}
					onSubmit={handleAddRule}
				/>
			</Modal>
			{
				isEditMode && (
					<div className={styles.inputWrapper}>
						<Input
							errorMessage={isSameName && checkRuleNameError ? undefined : checkRuleNameError}
							isMaxLengthCounter
							label='Rule Name'
							maxLength={MAX_50_LENGTH}
							name='name'
							onChange={validateNameHandler}
							placeholder='Enter rule name'
							register={register}
							required
							valueLength={name?.length}
						/>
						<div className={styles.separator}/>
						<div className={styles.title}>Schedule</div>
					</div>
				)
			}

			<div className={styles.workingDaysGrid}>
				{scheduleFields.map((field) => {
					const options: IGroupSelectItem[] = field.ranges?.map((range) => ({
						id: range.id,
						title: range.id,
						categoryName: field.weekDay.name,
					})) || [];

					return (
						<div
							key={field.weekDay.name}
							className={styles.workingDayCell}
						>
							<div className={styles.workingDayCellTitleWrapper}>
								<div className={styles.workingDayCellTitle}>
									<Checkbox
										checked={field.isEnabled}
										disabled={!field.ranges.length || !isEditMode}
										isFilledStyle
										name={field.weekDay.name}
										onChange={handleCheckboxChange(field)}
									/>
									{field.weekDay.title}
								</div>
								<div>
									{!!field.ranges.length && isEditMode &&(
										<DynamicIconWrapper
											icon={Icons.copy}
											isDisabled={!isEditMode}
											onClick={handleDuplicateRange(field)}
											tooltipLabel='Duplicate schedule'
											tooltipPlacement='top'
										/>
									)}
								</div>
							</div>
							{isEditMode && (
								<div
									className={styles.workingDayCellBtn}
									onClick={handleAddRange(field)}
								>
									<DynamicIconWrapper
										icon={Icons.add}
										isDisabled={!isEditMode}
									/>
									<span>Add time</span>
								</div>
							)}
							<ValueContent
								containerRef={containerRef}
								isAllItemsSelected={false}
								isColumn
								isDisabled={!field.isEnabled}
								menuPlacement={EChipsSelectMenuPlacement.bottomEnd}
								readOnly={!isEditMode}
								removeOptions={removeOptions}
								selectedListOfOptions={options}
								sortFn={sortRuleRanges}
							/>
						</div>
				);
			})}
			</div>
			{isEditMode && (
				<>
					<div className={styles.separator}/>
					<TextArea
						errorMessage={errors?.notes?.message}
						isMaxLengthCounter
						isNotResizable
						label='Notes'
						maxLength={MAX_500_LENGTH}
						name='note'
						placeholder='Enter notes'
						register={register}
						valueLength={note?.length || 0}
						withUseForms
					/>
				</>
			)}
			<SubmissionButtons
				disabledPositive={isEditMode ? isSubmitDisabled : false}
				isNegativeButtonNeeded={!isWithoutNegativeButton && isEditMode}
				isProceedButtonNeeded={!isEditMode && !isWithoutNegativeButton}
				negativeHandler={negativeHandler}
				negativeLabel='Cancel'
				positiveHandler={handleSubmit}
				positiveLabel={isEditMode ? 'Save' : 'Close'}
				proceedHandler={proceedHandler}
				proceedLabel='Edit'
			/>
		</div>
	);
};

export default memo(OperationWindowRulesForm);
